// Feature P9 (SimOptions.tyreTracks): matted grass under the mower's rear
// wheels. The sim keeps a level per cell in `grid.matted` and pushes the cell
// dirty when the level steps, so the turf canvas repaints only what changed:
// each tracked cell repaints once when it's stamped and TYRE_TRACK_FADE_STEPS
// times as it fades (once a second at most), never per frame.

import type { Ctx } from './ctx';
import { T_GRASS } from './layout';
import { TYRE_TRACK_FADE_S, trackLevel } from './vibe';

/** Rear wheels: this far behind the operator (m, × gameScale), and this share of the deck out to each side. */
const WHEEL_BACK_M = 0.25;
const WHEEL_SPAN = 0.45;
const MIN_SPEED = 0.1;

function stamp(ctx: Ctx, x: number, y: number): void {
  const g = ctx.grid;
  const m = g.matted;
  if (!m) return;
  const cx = Math.floor(x / g.cellM);
  const cy = Math.floor(y / g.cellM);
  if (cx < 0 || cy < 0 || cx >= g.w || cy >= g.h) return;
  const i = cy * g.w + cx;
  if (g.type[i] !== T_GRASS) return;
  if (ctx.trackT[i] < 0) ctx.trackList.push(i);
  ctx.trackT[i] = ctx.elapsedS;
  if (m[i] !== 255) {
    m[i] = 255;
    g.dirty.push(i);
  }
}

/** Mat the cells under both rear wheels (on the mower, moving). */
export function stampTracks(ctx: Ctx): void {
  const op = ctx.op;
  if (!op.onMower || Math.abs(op.speed) < MIN_SPEED) return;
  const fx = Math.cos(op.heading);
  const fy = Math.sin(op.heading);
  const back = WHEEL_BACK_M * ctx.gs;
  const lat = ctx.mower.deckM * WHEEL_SPAN;
  const bx = op.x - fx * back;
  const by = op.y - fy * back;
  stamp(ctx, bx - fy * lat, by + fx * lat);
  stamp(ctx, bx + fy * lat, by - fx * lat);
}

/** Step every fading track's level, once per elapsed second; drop the ones gone. */
export function fadeTracks(ctx: Ctx): void {
  const sec = Math.floor(ctx.elapsedS);
  if (sec === ctx.trackSec) return;
  ctx.trackSec = sec;
  const g = ctx.grid;
  const m = g.matted;
  if (!m) return;
  const list = ctx.trackList;
  for (let k = list.length - 1; k >= 0; k--) {
    const i = list[k];
    const age = ctx.elapsedS - ctx.trackT[i];
    const lv = trackLevel(age);
    if (lv !== m[i]) {
      m[i] = lv;
      g.dirty.push(i);
    }
    if (age >= TYRE_TRACK_FADE_S) {
      ctx.trackT[i] = -1;
      list[k] = list[list.length - 1];
      list.pop();
    }
  }
}
